import React from 'react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslatedMessages } from '../../../../hooks/useTranslatedMessages';
import DocumentReportAppliedFilters from './DocumentReportAppliedFilters';
import ReportMultiSelectFilter from './ReportMultiSelectFilter';
import ReportSingleSelectFilter from './ReportSingleSelectFilter';
import ReportTimePeriodFilter from './ReportTimePeriodFilter';
import {
  DOCUMENT_REPORT_ERROR_CODES,
  DOCUMENT_REPORT_FILTER_KEYS,
  MAX_TIME_PERIOD_DAYS,
} from '../../constants/documentReportFilterConstants';

const tooltipSlotProps = {
  tooltip: { sx: { maxWidth: 300 } },
};

const FilterErrorMessage = ({ children }) => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    <ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.main' }} />
    <Typography variant="caption" color="error.main">
      {children}
    </Typography>
  </Stack>
);

const DocumentReportFilterPanel = ({
  filters,
  appliedFilters,
  filterOptions,
  validationErrors,
  touched,
  dayCount,
  onFilterChange,
  onRemoveAppliedFilter,
  onClearAllFilters,
  onGenerate,
  generateDisabled = false,
  generateLoading = false,
  generateTooltip = '',
}) => {
  const { documentReports } = useTranslatedMessages();
  const docReport = documentReports?.document_report ?? {};
  const filterLabels = documentReports?.filters ?? {};
  const errorLabels = filterLabels.errors ?? {};

  const showPeriodError = touched && Boolean(validationErrors.timePeriod);
  const showTinError = touched && Boolean(validationErrors.tin);
  const showDocTypesError = touched && Boolean(validationErrors.docTypes);
  const showEDocError = touched && Boolean(validationErrors.eDoc);
  const showStatusError = touched && Boolean(validationErrors.status);
  const periodErrorMessage =
    validationErrors.timePeriod === DOCUMENT_REPORT_ERROR_CODES.RANGE_TOO_LONG
      ? (errorLabels.period_too_long ?? 'Date range cannot exceed 12 months.')
      : (errorLabels.period_required ?? 'Please select a start and end date.');
  const tinErrorMessage = errorLabels.tin_required ?? 'Please select at least one TIN.';
  const docTypesErrorMessage = errorLabels.document_type_required ?? 'Please select at least one document type.';
  const eDocErrorMessage = errorLabels.e_document_required ?? 'Please select at least one e-document type.';
  const statusErrorMessage = errorLabels.status_required ?? 'Please select at least one status.';

  const daysSelectedText = (filterLabels.days_selected ?? '{count} days selected (max {max})')
    .replace('{count}', String(dayCount))
    .replace('{max}', String(MAX_TIME_PERIOD_DAYS));

  const sharedMultiSelectProps = {
    searchPlaceholder: filterLabels.search ?? 'Search...',
    emptyText: filterLabels.no_results ?? 'No results.',
  };

  const { label: disabledLabel, docTypeTooltip } = filterOptions.disabledFilterLabels ?? {};

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: 'divider' }}>
      <Stack direction="row" alignItems="flex-end" flexWrap="wrap" useFlexGap spacing={1.25} sx={{ rowGap: 2 }}>
        <ReportMultiSelectFilter
          {...sharedMultiSelectProps}
          id="document-report-tin-filter"
          label={filterLabels.tin ?? 'TIN'}
          options={filterOptions.tinOptions}
          value={filters.tin}
          onChange={(value) => onFilterChange(DOCUMENT_REPORT_FILTER_KEYS.TIN, value)}
          loading={filterOptions.profilesLoading}
          required
          error={showTinError}
        />

        <ReportMultiSelectFilter
          {...sharedMultiSelectProps}
          id="document-report-doc-type-filter"
          label={filterLabels.document_type ?? 'Document Type'}
          options={filterOptions.docTypeOptions}
          value={filters.docTypes}
          onChange={(value) => onFilterChange(DOCUMENT_REPORT_FILTER_KEYS.DOC_TYPE, value)}
          required
          error={showDocTypesError}
          disabledLabel={disabledLabel}
          disabledTooltip={docTypeTooltip}
        />

        <ReportMultiSelectFilter
          {...sharedMultiSelectProps}
          id="document-report-edoc-filter"
          label={filterLabels.e_document ?? 'E-document'}
          options={filterOptions.eDocOptions}
          value={filters.eDoc}
          onChange={(value) => onFilterChange(DOCUMENT_REPORT_FILTER_KEYS.EDOC, value)}
          required
          error={showEDocError}
        />

        <ReportMultiSelectFilter
          {...sharedMultiSelectProps}
          id="document-report-status-filter"
          label={filterLabels.status ?? 'Status'}
          options={filterOptions.statusOptions}
          value={filters.status}
          onChange={(value) => onFilterChange(DOCUMENT_REPORT_FILTER_KEYS.STATUS, value)}
          required
          error={showStatusError}
        />

        <ReportSingleSelectFilter
          id="document-report-type-filter"
          label={filterLabels.report_type ?? 'Report Type'}
          options={filterOptions.reportTypeOptions}
          value={filters.reportType}
          onChange={(value) => onFilterChange(DOCUMENT_REPORT_FILTER_KEYS.REPORT_TYPE, value)}
          required
        />

        <ReportTimePeriodFilter
          id="document-report-period-filter"
          value={filters.timePeriod}
          onChange={(value) => onFilterChange(DOCUMENT_REPORT_FILTER_KEYS.TIME_PERIOD, value)}
          required
          error={showPeriodError}
        />

        <Tooltip title={generateTooltip} arrow placement="auto" slotProps={tooltipSlotProps}>
          <span style={{ display: 'inline-flex' }}>
            <Button
              id="generate-report"
              variant="contained"
              disabled={generateDisabled}
              onClick={onGenerate}
              startIcon={<DescriptionOutlinedIcon />}
              sx={{ height: 40, minWidth: 170, whiteSpace: 'nowrap' }}
            >
              {generateLoading
                ? (docReport.generating ?? 'Generating...')
                : (docReport.generate_report ?? 'Generate Report')}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
        {showTinError && <FilterErrorMessage>{tinErrorMessage}</FilterErrorMessage>}
        {showDocTypesError && <FilterErrorMessage>{docTypesErrorMessage}</FilterErrorMessage>}
        {showEDocError && <FilterErrorMessage>{eDocErrorMessage}</FilterErrorMessage>}
        {showStatusError && <FilterErrorMessage>{statusErrorMessage}</FilterErrorMessage>}
        {showPeriodError && <FilterErrorMessage>{periodErrorMessage}</FilterErrorMessage>}
        {dayCount > 0 && dayCount <= MAX_TIME_PERIOD_DAYS && (
          <Typography variant="caption" color="text.secondary">
            {daysSelectedText}
          </Typography>
        )}
      </Stack>

      <DocumentReportAppliedFilters
        appliedFilters={appliedFilters}
        onRemove={onRemoveAppliedFilter}
        onClearAll={onClearAllFilters}
      />
    </Paper>
  );
};

export default DocumentReportFilterPanel;
